import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { AxiosResponse } from 'axios';

import {
  ICreateDriverRequest,
  ICreateDriverResponse,
  IUpdateDriverRequest,
  IUpdateDriverResponse,
  IGetSingleDriverResponse,
  IDeleteDriversResponse,
  IDeleteDriversRequestFilters,
  IGetDriversFilters,
  IGetDriversResponse,
  ISuspendDriverResponse,
  ICancelSuspendDriverResponse,
  IGetDriverVehiclesResponse,
  IGetDriversSimpleListFilters,
} from 'models/http/drivers';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { HTTP } from './core.http';
import { IGetHistoryResponse } from 'models/http/history';
import DataUploadBaseHttp from './utils/data-upload.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import { IGetSimpleListResponse } from 'models/http/common';

class DriverHttp extends DataUploadBaseHttp implements HTTP<IDeleteDriversRequestFilters> {
  public static DataExportRoute = `${ModelsNames.Drivers}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.Drivers;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async getSimple(filters?: IGetDriversSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    const queryParams = prepareQPFromFilters({ withVehicle: filters?.withVehicle });

    return serverAxios
      .get(`/${this.route}/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
        params: queryParams,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async get(filters: IGetDriversFilters, abortController?: AbortController): Promise<IGetDriversResponse['records']> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`/${this.route}?${query}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriversResponse>) => {
        return response.data.records;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleDriverResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleDriverResponse>) => {
        return response.data;
      });
  }

  public async getDriverAvailableVehicles(id: number, abortController?: AbortController): Promise<IGetDriverVehiclesResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/vehicles`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverVehiclesResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateDriverRequest): Promise<ICreateDriverResponse> {
    return serverAxios
      .post(`/${this.route}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateDriverResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, driver: IUpdateDriverRequest): Promise<IUpdateDriverResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, driver, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateDriverResponse>) => {
        return response.data;
      });
  }

  public async suspend(id: number, reason: string): Promise<ISuspendDriverResponse> {
    return serverAxios.put(`/${this.route}/${id}/suspension`, { reason }).then((response: AxiosResponse<ISuspendDriverResponse>) => {
      return response.data;
    });
  }

  public async unSuspend(id: number): Promise<ICancelSuspendDriverResponse> {
    return serverAxios.delete(`/${this.route}/${id}/suspension`).then((response: AxiosResponse<ICancelSuspendDriverResponse>) => {
      return response.data;
    });
  }

  public async delete(ids: number[]): Promise<IDeleteDriversResponse>;
  public async delete(filters: IDeleteDriversRequestFilters): Promise<IDeleteDriversResponse>;
  public async delete(filters: number[] | IDeleteDriversRequestFilters): Promise<IDeleteDriversResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteDriversResponse>) => {
        return response.data;
      });
  }
}

export default DriverHttp;
